/**
 * Neutral endings (agion/Agion#1884): outcomes of a run that finished and
 * delivered its answer, but whose answer the user should check before relying
 * on it. The run is recorded as failed, yet the chat shows a neutral notice and
 * the activity log a warning row instead of the crash line.
 *
 * This is the one list every surface reads: the live and reopened chat notice,
 * the live activity-log severity and the persisted audit trail. Each outcome
 * carries one sentence that says what happened and what to do next.
 */

interface NeutralEndingCopy {
  /** i18n key of the sentence */
  key: string
  /** English fallback when the key is missing */
  fallback: string
}

export const NEUTRAL_ENDINGS = {
  criteria_not_met: {
    key: 'chat:customer.missionCriteriaNotMet',
    fallback: 'The run finished, but it did not meet all of its success criteria. Check the answer before you rely on it.',
  },
  no_successful_resource_evidence: {
    key: 'chat:customer.missionNoResourceEvidence',
    fallback: 'The run could not get any data from its connected systems, so the answer may be incomplete. Ask your administrator to check the connection before you rely on it.',
  },
} as const satisfies Record<string, NeutralEndingCopy>

export type NeutralEnding = keyof typeof NEUTRAL_ENDINGS

/** The neutral ending an outcome names, or undefined for every other outcome. */
export function neutralEnding(outcome: unknown): NeutralEnding | undefined {
  if (typeof outcome !== 'string') return undefined
  return Object.prototype.hasOwnProperty.call(NEUTRAL_ENDINGS, outcome) ? (outcome as NeutralEnding) : undefined
}

type Translate = (key: string, fallback: string) => string

/** The translated sentence for one neutral ending. */
export function neutralEndingMessage(t: Translate, outcome: NeutralEnding): string {
  const { key, fallback } = NEUTRAL_ENDINGS[outcome]
  return t(key, fallback)
}

/** The translated sentence for every neutral ending. */
export function neutralEndingMessages(t: Translate): Record<NeutralEnding, string> {
  const messages = {} as Record<NeutralEnding, string>
  for (const outcome of Object.keys(NEUTRAL_ENDINGS) as NeutralEnding[]) {
    messages[outcome] = neutralEndingMessage(t, outcome)
  }
  return messages
}
